/**
 * Registration tab – visually aligned to design
 * When a patient is selected from dashboard: show that patient's registration details in disabled (read-only) mode.
 * When no patient selected and user clicks "Create new patient": blank editable form.
 */

import { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  FormControlLabel,
  Switch,
  Alert,
  TextField,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";

import PersonIcon from "@mui/icons-material/Person";
import SaveIcon from "@mui/icons-material/Save";
import ClearIcon from "@mui/icons-material/Clear";

import FormField from "../Common/FormField";
import RightPanelTabs from "../RightPanel/RightPanelTabs";
import { RIGHT_PANEL_WIDTH, RIGHT_PANEL_WIDTH_XS } from "../RightPanel/constants";
import AmbientMicButton from "../AmbientMicButton/AmbientMicButton";
import { getApiBaseUrl } from "../../utils/websocket";
import { apiFetch } from "../../api/client";

import { useFormData } from "../../contexts/FormDataContext";
import { useConfidenceToggle } from "../../contexts/ConfidenceToggleContext";
import { useSession } from "../../contexts/SessionContext";
import { useUser } from "../../contexts/UserContext";
import { useBusiness } from "../../contexts/BusinessContext";
import WorkflowSessionBar from "../Workflow/WorkflowSessionBar";
import {
  validateRegistrationForm,
  firstRegistrationError,
} from "../../utils/registrationValidation";

const TAB_NAME = "Registration";

function generateUniqueId(): string {
  const hex = crypto.randomUUID().replace(/-/g, "").slice(0, 10).toUpperCase();
  return `MRN-${hex}`;
}

/** Registration data from API: { [fieldName]: { value, confidence?, source? } } */
type RegistrationData = Record<string, { value?: unknown }>;

export default function RegistrationPage() {
  const { patient, setPatient, loadPatients } = useUser();
  const { formData, saveFormData, clearFormForTab, updateField, getFieldValue } = useFormData();
  const { showConfidence, setShowConfidence } = useConfidenceToggle();
  const { session, formSessionOverride } = useSession();
  const { activeTenantId, activeEntityId, activeConfigId, setActiveEntityId, tenants } = useBusiness();
  const activeTenant = tenants.find((t) => t.tenant_id === activeTenantId) ?? null;
  const entities = activeEntityId
    ? (tenants.flatMap((t) => t.entities ?? []).filter((e) => e.entity_id === activeEntityId))
    : (activeTenant?.entities?.length
      ? activeTenant.entities
      : Array.from(
          new Map(
            tenants.flatMap((t) => t.entities ?? []).map((e) => [e.entity_id, e]),
          ).values(),
        ));
  const [registrationEntityId, setRegistrationEntityId] = useState<string>("");

  const [registrationData, setRegistrationData] = useState<RegistrationData | null>(null);
  const [registrationLoading, setRegistrationLoading] = useState(false);
  const [registrationError, setRegistrationError] = useState<string | null>(null);

  const [saveStatus, setSaveStatus] = useState<
    "idle" | "saving" | "success" | "error"
  >("idle");
  const [saveMessage, setSaveMessage] = useState("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [ageEnteredManually, setAgeEnteredManually] = useState(false);

  useEffect(() => {
    if (activeEntityId) {
      if (registrationEntityId !== activeEntityId) setRegistrationEntityId(activeEntityId);
      return;
    }
    if (registrationEntityId) return;
    if (entities.length) setRegistrationEntityId(entities[0].entity_id);
  }, [activeEntityId, entities, registrationEntityId]);

  // When a patient is selected, fetch their registration data for read-only display
  useEffect(() => {
    if (patient?.patient_id == null) {
      setRegistrationData(null);
      setRegistrationError(null);
      return;
    }
    let cancelled = false;
    setRegistrationLoading(true);
    setRegistrationError(null);
    const apiBaseUrl = getApiBaseUrl();
    fetch(`${apiBaseUrl}/api/patient-data/${patient.patient_id}/2tab-form`, {
      headers: { "ngrok-skip-browser-warning": "true" },
    })
      .then((res) => {
        if (!res.ok) throw new Error(res.statusText);
        return res.json();
      })
      .then((data) => {
        if (!cancelled && data?.Registration) setRegistrationData(data.Registration);
        else if (!cancelled) setRegistrationData({});
      })
      .catch((err) => {
        if (!cancelled) setRegistrationError(err.message || "Failed to load registration");
      })
      .finally(() => {
        if (!cancelled) setRegistrationLoading(false);
      });
    return () => { cancelled = true; };
  }, [patient?.patient_id]);

  const calculateAge = (dob: string) => {
    if (!dob) return "";

    const birthDate = new Date(dob);
    const today = new Date();

    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();

    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }

    return age.toString();
  };

  const getRegValue = (fieldName: string): string => {
    const v = registrationData?.[fieldName]?.value;
    if (v === null || v === undefined) return "";
    return String(v);
  };

  const isViewMode = Boolean(
    patient && (registrationData !== null || registrationLoading) && !formSessionOverride
  );

  const dateOfRegistration = getFieldValue(TAB_NAME, "date_of_registration");
  const dateOfBirth = getFieldValue(TAB_NAME, "date_of_birth", "");
  const hasDob = dateOfBirth != null && String(dateOfBirth).trim() !== "";

  // Auto-fill date of registration with today when empty (create mode), same pattern as Pre-Consultation visit_date
  useEffect(() => {
    if (isViewMode) return;
    if (dateOfRegistration == null || String(dateOfRegistration).trim() === "") {
      const today = new Date();
      const yyyy = today.getFullYear();
      const mm = String(today.getMonth() + 1).padStart(2, "0");
      const dd = String(today.getDate()).padStart(2, "0");
      updateField(TAB_NAME, "date_of_registration", `${yyyy}-${mm}-${dd}`);
    }
  }, [dateOfRegistration, updateField, isViewMode]);

  // Auto-generate unique ID once per new registration session
  useEffect(() => {
    if (isViewMode) return;
    const uniqueId = getFieldValue(TAB_NAME, "unique_id", "");
    if (uniqueId == null || String(uniqueId).trim() === "") {
      updateField(TAB_NAME, "unique_id", generateUniqueId());
    }
  }, [isViewMode, getFieldValue, updateField]);

  // Clear stale save status when transitioning back to create mode (patient deselected or new registration)
  useEffect(() => {
    if (!patient) {
      setSaveStatus("idle");
      setSaveMessage("");
      setFieldErrors({});
    }
  }, [patient]);

  const collectRegistrationValues = () => ({
    patient_name: String(getFieldValue(TAB_NAME, "patient_name", "") ?? ""),
    gender: String(getFieldValue(TAB_NAME, "gender", "") ?? ""),
    date_of_birth: String(getFieldValue(TAB_NAME, "date_of_birth", "") ?? ""),
    age: String(getFieldValue(TAB_NAME, "age", "") ?? ""),
    date_of_registration: String(getFieldValue(TAB_NAME, "date_of_registration", "") ?? ""),
  });

  const runRegistrationValidation = () => {
    const errors = validateRegistrationForm(collectRegistrationValues());
    setFieldErrors(errors);
    return errors;
  };

  const handleClear = () => {
    clearFormForTab(TAB_NAME);
    setSaveStatus("idle");
    setSaveMessage("");
    setFieldErrors({});
    setAgeEnteredManually(false);
  };

  const handleSave = async () => {
    const validationErrors = runRegistrationValidation();
    if (Object.keys(validationErrors).length > 0) {
      setSaveStatus("error");
      setSaveMessage(firstRegistrationError(validationErrors) || "Please fix the highlighted fields.");
      return;
    }

    setSaveStatus("saving");

    // Create new patient without session (no patient selected)
    if (!patient && !session) {
      if (!registrationEntityId) {
        setSaveStatus("error");
        setSaveMessage("Please select an entity before saving registration.");
        return;
      }
      const apiBaseUrl = getApiBaseUrl();
      const payload = { Registration: formData?.Registration ?? {} };
      try {
        const registerUrl = apiBaseUrl ? `${apiBaseUrl}/api/users/patients/register` : '/api/users/patients/register';
        if (registrationEntityId) setActiveEntityId(registrationEntityId);
        const res = await apiFetch(registerUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "ngrok-skip-browser-warning": "true",
            ...(registrationEntityId ? { "X-Entity-Id": registrationEntityId } : {}),
            ...(activeTenantId ? { "X-Tenant-Id": activeTenantId } : {}),
            ...(activeConfigId ? { "X-Config-Id": activeConfigId } : {}),
          },
          body: JSON.stringify(payload),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          setSaveStatus("error");
          setSaveMessage(data.detail || `Registration failed: ${res.status}`);
          return;
        }
        setSaveStatus("success");
        setSaveMessage("Patient registered successfully. You can now select this patient and start a session.");
        setPatient({
          patient_id: data.patient_id,
          mrn: data.mrn ?? "",
          name: data.name ?? "",
          age: data.age ?? 0,
          gender: data.gender ?? "",
          date_of_birth: data.date_of_birth ?? null,
          contact_number: data.contact_number ?? "",
        });
        loadPatients();
        clearFormForTab(TAB_NAME);
        return;
      } catch (e) {
        setSaveStatus("error");
        setSaveMessage(e instanceof Error ? e.message : "Registration failed");
        return;
      }
    }

    const result = await saveFormData();

    if (result.success) {
      setSaveStatus("success");
      setSaveMessage("Registration saved successfully.");
    } else {
      setSaveStatus("error");
      setSaveMessage(result.error || "Save failed");
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        gap: 2,
        p: 2,
        height: "100%",
        overflow: "hidden",
        flexDirection: { xs: "column", md: "row" },
      }}
    >
      {/* LEFT SIDE */}
      <Box
        sx={{
          // Match other tabs' mobile behavior: share vertical space with the right panel.
          flex: { xs: "1 1 0%", md: "1 1 60%" },
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          overflow: "auto",
        }}
      >
        <Box sx={{ flexShrink: 0, mb: 2 }}>
          <WorkflowSessionBar />
        </Box>
        {/* PAGE HEADER */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 2,
          }}
        >
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Patient Registration
            </Typography>

            <Typography variant="body2" sx={{ color: "#6b6b6b", mt: 0.5 }}>
              {patient
                ? "Selected patient details (read-only). To create a new record, go to Dashboard and click Add New Patient."
                : "Enter patient information to create a new record in the system"}
            </Typography>
          </Box>

          {/* AI Confidence Toggle */}
          <Box
            sx={{
              bgcolor: "#eef3fb",
              borderRadius: 20,
              px: 1.5,
              py: 0.2,
            }}
          >
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={showConfidence}
                  onChange={(_, v) => setShowConfidence(v)}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': { color: '#008AD2' },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#008AD2' },
                  }}
                />
              }
              label="AI Confidence"
              sx={{
                m: 0,
                "& .MuiFormControlLabel-label": {
                  fontSize: "0.85rem",
                  color: "#4a4a4a",
                },
              }}
            />
          </Box>
        </Box>

        {/* FORM CARD */}
        <Paper
          sx={{
            p: 2.5,
            borderRadius: 2,
            border: "1px solid #d7e2f0",
            backgroundColor: "#fbfdff",
          }}
        >
          {/* FORM HEADER */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mb: 2,
              gap: 1,
            }}
          >
            <PersonIcon sx={{ color: "#012676" }} />

            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Registration Form
            </Typography>
          </Box>

          <Box sx={{ mb: 2, display: "flex", flexWrap: "wrap", gap: 2 }}>
            <TextField
              select
              size="small"
              label="Register patient under entity"
              value={registrationEntityId}
              onChange={(e) => setRegistrationEntityId(String(e.target.value || ""))}
              sx={{ minWidth: 280 }}
              helperText={entities.length ? "Used for clinic scoping + access control" : "No entities available. Link user to an entity in Users & access."}
              disabled={isViewMode}
            >
              {entities.map((e) => (
                <MenuItem key={e.entity_id} value={e.entity_id}>
                  {e.name}
                </MenuItem>
              ))}
            </TextField>
            {!entities.length && (
              <Alert severity="warning" sx={{ alignSelf: 'center' }}>
                No entities available for this login. Link this user to an entity in Settings {'>'} Users & access.
              </Alert>
            )}
          </Box>

          {/* View mode: patient selected → show registration in disabled mode */}
          {isViewMode && registrationLoading && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 3 }}>
              <CircularProgress size={24} />
              <Typography variant="body2" color="text.secondary">Loading patient registration…</Typography>
            </Box>
          )}

          {isViewMode && registrationError && (
            <Alert severity="error" sx={{ mt: 1 }}>{registrationError}</Alert>
          )}

          {isViewMode && !registrationLoading && registrationData && (
            <Grid container spacing={1.8}>
              <Grid item xs={12} md={6}>
                <TextField fullWidth size="small" label="# Unique ID" value={getRegValue("unique_id")} InputProps={{ readOnly: true }} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth size="small" label="Date of Registration" type="date" value={getRegValue("date_of_registration")} InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth size="small" label="Full Name *" value={getRegValue("patient_name")} InputProps={{ readOnly: true }} />
              </Grid>
              <Grid item xs={12} md={6} />
              <Grid item xs={12} md={6}>
                <TextField fullWidth size="small" label="Date of Birth" type="date" value={getRegValue("date_of_birth")} InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth size="small" label="# Age (years)" value={getRegValue("age")} InputProps={{ readOnly: true }} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth size="small" label="Gender *" value={getRegValue("gender")} InputProps={{ readOnly: true }} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth size="small" label="National ID Number" value={getRegValue("national_id_number")} InputProps={{ readOnly: true }} />
              </Grid>
            </Grid>
          )}

          {/* Create mode: no patient selected → editable form */}
          {!isViewMode && (
          <>
          <Grid container spacing={1.8}>
            {/* Row 1 */}
            <Grid item xs={12} md={6}>
              <FormField
                tabName={TAB_NAME}
                fieldName="unique_id"
                label="# Unique ID"
                placeholder="Auto-generated"
                size="small"
                fullWidth
                disabled
                InputProps={{ readOnly: true }}
                sx={{ "& .MuiInputBase-root": { bgcolor: "action.hover" } }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormField
                tabName={TAB_NAME}
                fieldName="date_of_registration"
                label="Date of Registration"
                type="date"
                InputLabelProps={{ shrink: true }}
                size="small"
                fullWidth
              />
            </Grid>

            {/* Row 2 */}
            <Grid item xs={12} md={6}>
              <FormField
                tabName={TAB_NAME}
                fieldName="patient_name"
                label="Full Name *"
                placeholder="Enter patient's full name"
                size="small"
                required
                fullWidth
                error={!!fieldErrors.patient_name}
                helperText={fieldErrors.patient_name}
                onManualChange={() => {
                  if (fieldErrors.patient_name) {
                    setFieldErrors((prev) => { const next = { ...prev }; delete next.patient_name; return next; });
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} md={6} />

            {/* Row 3 */}
            <Grid item xs={12} md={6}>
              <FormField
                tabName={TAB_NAME}
                fieldName="date_of_birth"
                label="Date of Birth"
                type="date"
                InputLabelProps={{ shrink: true }}
                size="small"
                fullWidth
                disabled={ageEnteredManually && !hasDob}
                error={!!fieldErrors.date_of_birth}
                helperText={fieldErrors.date_of_birth}
                inputProps={{ max: new Date().toISOString().slice(0, 10) }}
                onManualChange={(dob: string) => {
                  if (dob == null || String(dob).trim() === "") {
                    updateField(TAB_NAME, "age", "");
                    setAgeEnteredManually(false);
                    if (fieldErrors.date_of_birth) {
                      setFieldErrors((prev) => { const next = { ...prev }; delete next.date_of_birth; return next; });
                    }
                    return;
                  }
                  setAgeEnteredManually(false);
                  const age = calculateAge(dob);
                  updateField(TAB_NAME, "age", age);
                  if (fieldErrors.date_of_birth || fieldErrors.age) {
                    setFieldErrors((prev) => { const next = { ...prev }; delete next.date_of_birth; delete next.age; return next; });
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormField
                tabName={TAB_NAME}
                fieldName="age"
                label="# Age (years)"
                placeholder={hasDob ? "Calculated from date of birth" : "Optional — enter age or leave blank"}
                type="number"
                size="small"
                fullWidth
                disabled={hasDob}
                InputProps={hasDob ? { readOnly: true } : undefined}
                sx={hasDob ? { "& .MuiInputBase-root": { bgcolor: "action.hover" } } : undefined}
                error={!!fieldErrors.age}
                helperText={fieldErrors.age}
                inputProps={{ min: 1, max: 120 }}
                onManualChange={(value: string) => {
                  if (hasDob) return;
                  const trimmed = value == null ? "" : String(value).trim();
                  setAgeEnteredManually(trimmed !== "");
                  if (fieldErrors.age) {
                    setFieldErrors((prev) => { const next = { ...prev }; delete next.age; return next; });
                  }
                }}
              />
            </Grid>

            {/* Row 4 */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small" required error={!!fieldErrors.gender} sx={{ minWidth: 120 }}>
                <InputLabel id="registration-gender-label">Gender *</InputLabel>
                <Select
                  labelId="registration-gender-label"
                  id="registration-gender"
                  value={getFieldValue(TAB_NAME, "gender", "") || ""}
                  label="Gender *"
                  onChange={(e) => {
                    updateField(TAB_NAME, "gender", e.target.value);
                    if (fieldErrors.gender) {
                      setFieldErrors((prev) => { const next = { ...prev }; delete next.gender; return next; });
                    }
                  }}
                >
                  <MenuItem value="">Select</MenuItem>
                  <MenuItem value="Male">Male</MenuItem>
                  <MenuItem value="Female">Female</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </Select>
                {fieldErrors.gender && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                    {fieldErrors.gender}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormField
                tabName={TAB_NAME}
                fieldName="national_id_number"
                label="National ID Number"
                placeholder="Enter national ID number"
                size="small"
                fullWidth
              />
            </Grid>
          </Grid>

          {/* Divider */}
          <Box
            sx={{
              borderTop: "1px solid #e0e0e0",
              mt: 3,
              pt: 2,
            }}
          >
            {/* Buttons */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 2,
              }}
            >
              <Button
                variant="outlined"
                startIcon={<ClearIcon />}
                onClick={handleClear}
                sx={{
                  borderColor: "#d32f2f",
                  color: "#d32f2f",
                }}
              >
                Clear Form
              </Button>

              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={saveStatus === "saving"}
                sx={{
                  bgcolor: "#012676",
                  "&:hover": { bgcolor: "#001f5c" },
                }}
              >
                {saveStatus === "saving"
                  ? "Saving..."
                  : "Save Registration"}
              </Button>
            </Box>

            {/* Alerts */}
            {saveStatus === "success" && (
              <Alert severity="success" sx={{ mt: 2 }}>
                {saveMessage}
              </Alert>
            )}

            {saveStatus === "error" && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {saveMessage}
              </Alert>
            )}
          </Box>
          </>
          )}
        </Paper>

        {/* NOTE */}
        <Alert severity="info" icon={false} sx={{ mt: 3 }}>
          {formSessionOverride
            ? "Editing registration for a past visit session. Save updates that visit's stored data."
            : patient
              ? "Showing selected patient's registration details (read-only). To create a new patient, use Dashboard → Add New Patient."
              : "Fields marked with * are required. The Unique ID can be auto-generated by the system or entered manually. Date of birth and age are optional; when you set a date of birth, age is filled automatically, and you can still edit age if needed."}
        </Alert>

        <AmbientMicButton placement="fixed" />
      </Box>

      {/* RIGHT PANEL */}
      <Box
        sx={{
          width: { xs: RIGHT_PANEL_WIDTH_XS, md: RIGHT_PANEL_WIDTH },
          flexShrink: 0,
          minHeight: 0,
          flex: { xs: '1 1 0%', md: '0 0 auto' },
          height: '100%',
          display: "flex",
          overflow: "hidden",
        }}
      >
        <RightPanelTabs
          defaultTab={formSessionOverride ? 'summary' : 'currentVisit'}
          sessionId={session?.session_id ?? null}
          showCurrentVisitContent={!!session && session.status === 'active'}
        />
      </Box>
      
    </Box>
  );
}